import { motion } from 'framer-motion';
import { capitalize } from 'lodash';
import { Fragment, useMemo } from 'react';
import { FiX } from 'react-icons/fi';
import { GiSettingsKnobs } from 'react-icons/gi';

import { FieldAssistant } from '../FieldAssistant';
import { DefaultTabsProps } from '../Tabs';
import { TabType } from '../Types';

import { FormFilterPlayers } from './FormFilterPlayers';
import { HeaderMyTeam } from './HeaderMyTeam';
import { MoneyActionsDisplayer } from './MoneyActionsDisplayer';
import { PlayerPicker } from './PlayerPicker';

import { Button, NavBar, Tabs } from '@/components';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useGetPlayerPositionByAbbreviation } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { EnumTeamContext, TemplatePlayer } from '@/types/team';
import { cn, FADE_IN_OUT, SLIDE_IN_OUT_LEFT } from '@/utils';

// Base Props
type OnClickType = {
  onClick: () => void;
  context?: EnumTeamContext;
};

type ContextType = {
  context?: EnumTeamContext;
};

type SelectPLayerOnList = {
  handleSelectPlayerOnList: (player: TemplatePlayer) => void;
};
// End Base Props

type SidebarHeaderInfoProps = OnClickType;
type DesktopSidebarProps = OnClickType & ContextType & SelectPLayerOnList;
type MobileSidebarProps = OnClickType &
  ContextType &
  SelectPLayerOnList & {
    title: string;
    backAction: () => void;
  };

export function SidebarHeaderInfo({
  onClick,
  context = EnumTeamContext.Team11,
}: SidebarHeaderInfoProps) {
  const { state } = useTeamStore();
  const { isMobile, isDesktop } = useIsDesktop();

  return (
    <MoneyActionsDisplayer
      actions={
        <Button
          className={cn('py-2 flex items-center gap-x-1 text-xs', {
            'bg-[#CCD9FF] border-gdt-blue': state.showFiltersPlayersList,
          })}
          type="button"
          variant="secondary"
          onClick={onClick}
        >
          {(!state.showFiltersPlayersList && isDesktop) || isMobile ? (
            <>
              <GiSettingsKnobs className="w-4 h-4 rotate-90" /> Filtros
            </>
          ) : (
            <>
              <FiX className="w-4 h-4 rotate-90" /> Cerrar
            </>
          )}
        </Button>
      }
      context={context}
    />
  );
}

export function DesktopSidebar({
  onClick,
  handleSelectPlayerOnList,
  context,
}: DesktopSidebarProps) {
  const { state } = useTeamStore();

  const isContextBuild =
    context &&
    [EnumTeamContext.Build11, EnumTeamContext.Build5].includes(context);

  const isFilter = context?.includes(EnumTeamContext.FilterTeam11)
    ? EnumTeamContext.FilterTeam11
    : EnumTeamContext.FilterTeam5;

  const isTeam = context?.includes(EnumTeamContext.Team11)
    ? EnumTeamContext.Team11
    : EnumTeamContext.Team5;

  return (
    <Fragment>
      {isContextBuild ? (
        <SidebarHeaderInfo context={context} onClick={onClick} />
      ) : (
        <HeaderMyTeam
          context={
            state.showPlayersList || state.isBeforeVeda ? isFilter : isTeam
          }
          onClick={onClick}
        />
      )}
      <div
        className={cn(`lg:h-[540px] lg:bg-gdt-gray-6`, {
          'lg:h-full overflow-hidden': isContextBuild,
        })}
      >
        <PlayerPicker
          context={context}
          handleSelectPlayerOnList={handleSelectPlayerOnList}
        />
      </div>
      {state.showFiltersPlayersList && (
        <motion.div
          animate={'show'}
          className={cn(
            'bg-gdt-gray-7 top-[58px] h-[calc(100%-36px)] absolute left-0 w-full',
            {
              'h-[calc(100%-58px)]': context === EnumTeamContext.FilterTeam11,
              'lg:h-[calc(100%-58px)]': isContextBuild,
            }
          )}
          initial="hide"
          variants={SLIDE_IN_OUT_LEFT}
        >
          <FormFilterPlayers context={context} />
        </motion.div>
      )}
    </Fragment>
  );
}

// This component needs to be wrapped in a <SliderOver /> component, just as Team11.tsx
export function MobileSidebar({
  backAction,
  onClick,
  handleSelectPlayerOnList,
  context,
  title,
}: MobileSidebarProps) {
  const { state } = useTeamStore();
  const position = useGetPlayerPositionByAbbreviation(state.selectedPosition);
  const { tab, handleTabChanges } = useHandleChangesTabs();

  const isContextBuild =
    context &&
    [EnumTeamContext.Build11, EnumTeamContext.Build5].includes(context);

  const isFilter = context?.includes(EnumTeamContext.FilterTeam11)
    ? EnumTeamContext.FilterTeam11
    : EnumTeamContext.FilterTeam5;

  const isTeam = context?.includes(EnumTeamContext.Team11)
    ? EnumTeamContext.Team11
    : EnumTeamContext.Team5;

  const arrTabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: capitalize(position) },
      {
        name: 'Ayudante de campo',
      },
    ];
  }, [position]);

  const isFilterTeamVariant =
    context &&
    [EnumTeamContext.FilterTeam11, EnumTeamContext.FilterTeam5].includes(
      context
    );

  const tabs = isFilter && (
    <Tabs
      activeTab={tab}
      tabs={arrTabs}
      variant={isFilterTeamVariant ? 'underline' : 'primary'}
      onClick={(tab: TabType) => handleTabChanges(tab)}
    />
  );

  return (
    <NavBar
      mobile={{
        leftContent: {
          backAction,
        },
        centerContent: {
          title,
        },
      }}
    >
      <div className="flex flex-col flex-1">
        {/* TAB DE SELECCION DE JUGADORES */}
        {tab === TabType.FirstTab ? (
          isContextBuild ? (
            <>
              <SidebarHeaderInfo context={context} onClick={onClick} />
              <section className="flex flex-col flex-1 bg-gdt-gray-7">
                <PlayerPicker
                  context={context}
                  handleSelectPlayerOnList={handleSelectPlayerOnList}
                />
              </section>
            </>
          ) : (
            <>
              <div className="bg-white">{tabs}</div>
              <div className="bg-gdt-gray-7">
                <motion.div
                  animate={'show'}
                  initial={'hide'}
                  variants={FADE_IN_OUT}
                >
                  <HeaderMyTeam
                    context={state.showPlayersList ? isFilter : isTeam}
                    onClick={onClick}
                  />
                  <section className="flex flex-col flex-1 bg-gdt-gray-7 ">
                    <PlayerPicker
                      context={context}
                      handleSelectPlayerOnList={handleSelectPlayerOnList}
                    />
                  </section>
                </motion.div>
              </div>
            </>
          )
        ) : (
          <>
            {/* TAB AYUDANTE DE CAMPO */}
            <div className="bg-white">{tabs}</div>
            <div className="bg-gdt-gray-7">
              <FieldAssistant
                context={context}
                showTitle={false}
                variant="EMBED"
              />
            </div>
          </>
        )}
      </div>
    </NavBar>
  );
}
